// Die 5 Maße einer Aufmaßzeile, wie sie auf dem Bildschirm heissen.
//
// ORIGINALTEXT MARVIN (10.09.2026)
//   „der tiefbauer tippt bei der entsprechenden position ein, die zur
//    errechnung notwendig sind. dann kommt ein wert raus in m2, was dann
//    alles zusammen in dem LEB eingetragen wird."
//
// HIER STEHT KEINE RECHNUNG, und das ist der ganze Punkt dieser Datei. Was aus
// den Maßen wird, rechnet der SERVER (`api/src/regeln/menge.ts`) und schickt es
// mit: die Formel, ihr Ergebnis und die Liste der Felder, die für diese
// Einheit Faktoren sind. Eine zweite Fassung der Rundung im Browser liefe beim
// ersten Nachtrag auseinander, und dann stünde auf dem Bildschirm eine andere
// Zahl als in der Datei, die ins Portal hochgeladen wird - dieselbe
// Entscheidung wie bei `meterZwischen` im Tiefbau.
//
// Sie steht OHNE JSX daneben, damit ein Prüfstand sie LADEN statt SUCHEN kann:
// eine Textsuche misst, dass etwas dasteht, nicht, dass es stimmt.

/** Die Spaltennamen von `aufmass_position`. Bezeichner, also ohne Umlaut. */
export const MASSFELDER = [
  'laenge', 'breite', 'tiefe', 'dicke', 'flaeche',
] as const;

export type Massfeld = typeof MASSFELDER[number];

/**
 * Text für Menschen, mit Umlaut.
 *
 * DIESELBE LISTE STEHT IM SERVER (`FELDNAME` in `regeln/menge.ts`), und ein
 * Prüfstand legt beide nebeneinander. Zwei Fassungen einer Aufschrift sind
 * zwei Aufschriften, und dann heisst dasselbe Feld auf zwei Bildschirmen
 * verschieden.
 */
export const FELDNAME: Record<Massfeld, string> = {
  laenge: 'Länge',
  breite: 'Breite',
  tiefe: 'Tiefe',
  dicke: 'Dicke',
  flaeche: 'Fläche',
};

/** Wie das Feld heisst, auch wenn es die Liste nicht kennt. */
export function feldname(f: string): string {
  return (FELDNAME as Record<string, string>)[f] ?? f;
}

/** Steht an dieser Zeile überhaupt ein Maß? */
export function hatMass(p: Record<string, unknown>): boolean {
  return MASSFELDER.some((f) => p[f] !== null && p[f] !== undefined
                                && String(p[f]) !== '');
}

/**
 * WER MASSE EINTRÄGT, TIPPT KEINE MENGE.
 *
 * Das Mengenfeld ist gesperrt, sobald ein Maß dasteht - sonst stünden 2
 * Wahrheiten über dieselbe Zeile nebeneinander, und welche gilt, hinge daran,
 * welche zuletzt gespeichert wurde. Dieselbe Entscheidung wie beim Kästchen
 * „Leerrohr liegt", das die Oberflächenauswahl sperrt.
 *
 * Der RIEGEL dazu sitzt im Server: eine Maske ist eine Höflichkeit, und ein
 * Werkzeug geht an ihr vorbei.
 */
export function mengeGesperrt(masse: Record<string, unknown>): boolean {
  return hatMass(masse);
}

/**
 * Was unter den Feldern steht.
 *
 * DREI AUSGÄNGE, UND JEDER SAGT ETWAS ANDERES. Es gibt eine Formel; es gibt
 * für diese Einheit nichts zu rechnen; es fehlt noch ein Maß. Ein Satz für
 * alle drei beantwortet keinen von ihnen.
 */
export function formelsatz(
  formel: string | null | undefined,
  einheit: string | null | undefined,
  faktorfelder: string[] | null | undefined
): string {
  const felder = faktorfelder ?? [];
  if (formel) return `Daraus wird die Menge: ${formel}`;
  if (!felder.length) {
    return `Zu einer Position in „${einheit ?? '?'}" gibt es nichts zu rechnen `
         + '- hier steht die Menge direkt.';
  }
  return `In ${einheit} wird ${felder.map(feldname).join(' mal ')} gerechnet.`;
}

/**
 * Die drei Rollen, die ein Maß an einer Position spielen kann.
 *
 * SIE WERDEN HIER NICHT GERECHNET, SIE KOMMEN MIT. `massfelder` in
 * `api/src/regeln/menge.ts` sagt je Position, welche Felder dastehen und
 * welche Rolle jedes spielt; hier steht nur, wie das auf dem Bildschirm
 * heisst. Eine zweite Fassung der Regel wäre an dem Tag falsch, an dem eine
 * fünfte Einheit dazukommt - und sie wäre lautlos falsch, denn ein Feld zu
 * viel sieht aus wie ein Feld.
 */
export type Feldrolle = 'faktor' | 'gemessen' | 'erfasst';

export type Massfeldanzeige = { feld: string; rolle: Feldrolle };

/**
 * Was unter einem Feld steht - in EINEM Wort.
 *
 * DREI ROLLEN, DREI MARKEN. Ein Feld, neben dem nicht steht, ob es in die
 * Menge geht, ist eine Frage ohne Antwort: wer die Tiefe einträgt und die
 * Menge nicht steigen sieht, hält den Bildschirm für kaputt. Der SATZ dazu
 * steht einmal unter der Reihe (`zulagesatz`) und nicht an jedem Feld - drei
 * gleiche Sätze nebeneinander sind eine Zeile, die niemand liest.
 */
export function rollentext(rolle: Feldrolle): string {
  if (rolle === 'faktor') return 'Faktor';
  if (rolle === 'gemessen') return 'gemessen';
  return 'Zulage';
}

/**
 * Warum ein Maß dasteht und trotzdem nicht in die Menge geht.
 *
 * EIN FELD, DAS ERFASST WIRD, IST NICHT DASSELBE WIE EIN FAKTOR. Tiefe und
 * Dicke begründen die Zulagepositionen („Mehrtiefe je 15 cm", „Mehrdicke
 * Asphalt/Beton je 4 cm") - eigene Ordnungszahlen mit eigener Menge. Wer sie
 * mitmultipliziert, macht aus einer Fläche ein Volumen.
 *
 * Leer, wo es nichts zu erklären gibt: ein Satz, der immer dasteht, wird auch
 * an dem Tag nicht gelesen, an dem er zutrifft.
 */
export function zulagesatz(felder: { feld: string; rolle: Feldrolle }[]): string {
  const zu = (felder ?? []).filter((f) => f.rolle === 'erfasst')
    .map((f) => feldname(f.feld));
  if (!zu.length) return '';
  /* KOMMA, KOMMA, UND. „Breite und Tiefe und Dicke" ist keine Aufzählung,
     sondern dreimal dasselbe Wort dazwischen. */
  const liste = zu.length > 1
    ? `${zu.slice(0, -1).join(', ')} und ${zu[zu.length - 1]}` : zu[0];

  return zu.length > 1
    ? `${liste} gehen nicht in die Menge - sie begründen die `
      + 'Zulagepositionen mit eigener Ordnungszahl.'
    : `${zu[0]} geht nicht in die Menge - sie begründet die Zulageposition `
      + 'mit eigener Ordnungszahl.';
}

/**
 * Die Maße einer Zeile, wie sie in die Eingabefelder gehören.
 *
 * MIT KOMMA, denn so liest sie jeder in diesem Haus. Was der Server annimmt,
 * entscheidet er selbst (`alsMenge` kennt beide Trennzeichen); hier steht, was
 * ein Mensch vor sich sieht.
 */
export function massAus(p: Record<string, unknown>): Record<string, string> {
  const raus: Record<string, string> = {};
  for (const f of MASSFELDER) {
    const w = p[f];
    raus[f] = w === null || w === undefined ? '' : String(w).replace('.', ',');
  }
  return raus;
}

/**
 * Die Maße, wie sie zum Server gehen.
 *
 * EIN LEERES FELD NIMMT EIN MASS WEG, und das ist Absicht: anders liesse sich
 * ein Tippfehler nie zurücknehmen. Es geht deshalb als `null` hinaus und nicht
 * als fehlender Schlüssel - ein fehlendes Feld hiesse „unverändert".
 */
export function massHinaus(
  m: Record<string, string>
): Record<string, string | null> {
  const raus: Record<string, string | null> = {};
  for (const f of MASSFELDER) {
    const w = (m[f] ?? '').trim();
    raus[f] = w === '' ? null : w;
  }
  return raus;
}
